import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
var onRight = new Set(['€']);
var currencySymbols = {
  USD: '$',
  EUR: '€',
  GBP: '£'
};
/** @component */

export var Price =
/*#__PURE__*/
function (_React$PureComponent) {
  _inheritsLoose(Price, _React$PureComponent);

  function Price() {
    return _React$PureComponent.apply(this, arguments) || this;
  }

  var _proto = Price.prototype;

  _proto.formatCurrency = function formatCurrency(amount) {
    var currency = this.props.currency;
    var renderCurrency = currencySymbols[currency] || currency;

    if (onRight.has(renderCurrency)) {
      var NBSP = "\xA0";
      return "" + amount + NBSP + renderCurrency;
    }

    return "" + renderCurrency + amount;
  };

  _proto.formatAmount = function formatAmount() {
    var _this$props = this.props,
        _this$props$amount = _this$props.amount,
        amount = _this$props$amount === void 0 ? '' : _this$props$amount,
        formatNumber = _this$props.formatNumber,
        trimZeros = _this$props.trimZeros; // Detect potential errors calculating prices.

    if (Number.isNaN(amount)) {
      throw new Error('<Price> was given a NaN amount.');
    }

    var formatted = formatNumber(amount);

    if (trimZeros && formatted) {
      formatted = formatted.replace(/[.,]0+$/, '');
    }

    return formatted;
  };

  _proto.formatAriaLabel = function formatAriaLabel(displayAmount) {
    var ariaLabel = this.props.ariaLabel;

    if (typeof ariaLabel === 'function') {
      return ariaLabel(this.props, displayAmount);
    }

    return ariaLabel;
  };

  _proto.render = function render() {
    var _this$props2 = this.props,
        className = _this$props2.className,
        old = _this$props2.old,
        allowBFLocalization = _this$props2.allowBFLocalization;
    var displayAmount = this.formatCurrency(this.formatAmount());
    var ariaLabel = this.formatAriaLabel(displayAmount);
    var classNames = [];

    if (allowBFLocalization) {
      classNames.push('bfx-price');
    }

    if (old && allowBFLocalization) {
      classNames.push('bfx-original-price');
    }

    if (className) {
      classNames.push(className);
    }

    return React.createElement("span", {
      className: classNames.join(' '),
      "aria-label": ariaLabel
    }, displayAmount);
  };

  return Price;
}(React.PureComponent);

_defineProperty(Price, "propTypes", {
  /**
   * The numeric price amount.
   */
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  /**
   * Class name(s) to apply to the wrapper element.
   */
  className: PropTypes.string,

  /**
   * The text color to show the price in.
   */
  color: PropTypes.string,

  /**
   * The currency code or symbol to render.
   */
  currency: PropTypes.string,

  /**
   * Whether to remove trailing `.00` from whole-number prices.
   */
  trimZeros: PropTypes.bool,

  /**
   * Whether this is an 'old' price (for example, the retail price when there
   * is also a discounted one shown). By default, this will render the price
   * with a strikethrough, but it can be customized via the `price.oldStyle`
   * theme property.
   */
  old: PropTypes.bool,

  /**
   * Whether this is a 'special' price (for example, a sale price, promotional
   * discount, VIP price, etc.) By default, this does not change the rendering
   * in any way, but it can be customized via the `price.specialStyle` theme
   * property.
   */
  special: PropTypes.bool,

  /**
   * The ARIA label to use on the price wrapper. This can be a string, or a
   * function that accepts the input props and the final formatted price and
   * returns a string. This is useful for things like `old` prices, since
   * screen readers do not inform the user that the price is rendered with a
   * strikethrough. By default, this returns `"was: <displayAmount>"` if `old`
   * is true, `"now: <displayAmount>"` if `special` is true, and nothing
   * otherwise.
   */
  ariaLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * Handle converting the number to a price. This is useful for localization.
   */
  formatNumber: PropTypes.func,

  /**
   * whether or not to add the class to have Borderfree localize the price
   */
  allowBFLocalization: PropTypes.bool
});

_defineProperty(Price, "defaultProps", {
  currency: '$',
  trimZeros: true,
  old: false,
  special: false,
  ariaLabel: function ariaLabel(props, displayAmount) {
    if (props.old) {
      return "was: " + displayAmount;
    } else if (props.special) {
      return "now: " + displayAmount;
    }
  },
  formatNumber: function formatNumber(value) {
    return typeof value === 'number' ? value.toFixed(2) : value;
  },
  allowBFLocalization: true
});

export default styled(Price).withConfig({
  displayName: "Price",
  componentId: "sc-1tftzty-0"
})(["\n  ", ";\n  /* Allow themes to specify their default special price styling via price.specialStyle. */\n  ", "\n  /* Allow themes to specify their default old price styling via price.oldStyle. */\n  ", "\n  /* If provided, the color prop takes precedence. */\n  ", "\n"], function (props) {
  return props.old ? 'text-decoration: line-through;' : '';
}, function (props) {
  return props.theme.price && props.special ? props.theme.price.specialStyle : '';
}, function (props) {
  return props.theme.price && props.old ? props.theme.price.oldStyle : '';
}, function (props) {
  return props.color ? "color: " + props.color + ";" : '';
});